import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSesion } from '../contexto/Sesion.js';
import { abrirPdf, api, ErrorApi } from '../api/cliente.js';
import {
  COLOR_ESTADO_FACTURA,
  ETIQUETA_ESTADO_FACTURA,
  formatearMonedaArs,
  type FacturaDTO,
  type ReporteHonorariosMedicoDTO,
} from '@ausentia/shared';

export function Perfil() {
  const { medico, salir, refrescarPerfil } = useSesion();
  const navegar = useNavigate();
  
  // Rango de fechas del mes actual por defecto
  const [desde, setDesde] = useState(() => {
    const hoy = new Date();
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    return `${hoy.getFullYear()}-${mes}-01`;
  });
  const [hasta, setHasta] = useState(() => {
    const hoy = new Date();
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();
    return `${hoy.getFullYear()}-${mes}-${String(ultimoDia).padStart(2, '0')}`;
  });

  const [datosHonorarios, setDatosHonorarios] = useState<ReporteHonorariosMedicoDTO | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ADR-0010: emisión de la factura de honorarios al operador (cliente).
  const [facturas, setFacturas] = useState<FacturaDTO[]>([]);
  const [emitiendo, setEmitiendo] = useState(false);
  const [facturaError, setFacturaError] = useState('');

  // Clave de firma por matrícula (opcional): la gestiona solo el médico.
  const [formClave, setFormClave] = useState<'oculto' | 'definir' | 'quitar'>('oculto');
  const [clavePassword, setClavePassword] = useState('');
  const [clave1, setClave1] = useState('');
  const [clave2, setClave2] = useState('');
  const [claveError, setClaveError] = useState('');
  const [claveOk, setClaveOk] = useState('');
  const [claveGuardando, setClaveGuardando] = useState(false);

  function abrirFormClave(modo: 'definir' | 'quitar') {
    setFormClave(modo);
    setClavePassword('');
    setClave1('');
    setClave2('');
    setClaveError('');
    setClaveOk('');
  }

  async function guardarClave() {
    setClaveError('');
    if (formClave === 'definir' && clave1 !== clave2) {
      setClaveError('Las claves no coinciden.');
      return;
    }
    setClaveGuardando(true);
    try {
      if (formClave === 'definir') {
        await api.put('/api/perfil/clave-firma', { password: clavePassword, clave: clave1 });
        setClaveOk('Listo. Desde ahora firmás ingresando tu clave.');
      } else {
        await api.delete('/api/perfil/clave-firma', { password: clavePassword });
        setClaveOk('Quitaste tu clave. Volvés a firmar con el código por email.');
      }
      await refrescarPerfil();
      setFormClave('oculto');
    } catch (err) {
      setClaveError(err instanceof ErrorApi ? err.message : String(err));
    } finally {
      setClaveGuardando(false);
    }
  }

  async function cargarFacturas() {
    try {
      setFacturas(await api.get<FacturaDTO[]>('/api/medico/facturas'));
    } catch {
      /* el listado de facturas no debe bloquear la vista de honorarios */
    }
  }
  useEffect(() => {
    if (medico) void cargarFacturas();
  }, [medico]);

  // Solapamiento, no igualdad exacta — mismo criterio que el servidor
  // (facturas.ts): un período que se superpone con uno ya facturado también
  // debe deshabilitar el botón, no solo el período idéntico.
  const yaFacturado = facturas.some(
    (f) => f.periodo.desde <= hasta && f.periodo.hasta >= desde && f.estado !== 'ANULADA',
  );

  async function emitirFactura() {
    setEmitiendo(true);
    setFacturaError('');
    try {
      await api.post<FacturaDTO>('/api/medico/facturas', { desde, hasta });
      await cargarFacturas();
    } catch (err) {
      setFacturaError(err instanceof ErrorApi ? err.message : 'No se pudo emitir la factura');
    } finally {
      setEmitiendo(false);
    }
  }

  async function verFactura(id: string) {
    try {
      await abrirPdf(`/api/medico/facturas/${id}/pdf`);
    } catch (err) {
      setFacturaError(err instanceof ErrorApi ? err.message : String(err));
    }
  }

  useEffect(() => {
    if (!medico) return;
    setCargando(true);
    setError(null);
    api.get<ReporteHonorariosMedicoDTO>(`/api/medico/honorarios?desde=${desde}&hasta=${hasta}`)
      .then(res => {
        setDatosHonorarios(res);
      })
      .catch((err: unknown) => {
        setError(err instanceof ErrorApi ? err.message : 'Error al cargar honorarios');
      })
      .finally(() => {
        setCargando(false);
      });
  }, [medico, desde, hasta]);

  if (!medico) return null;

  return (
    <div className="contenido" style={{ animation: 'fadeInUp 0.3s ease forwards' }}>
      <h2 style={{ marginTop: 10 }}>Perfil</h2>
      
      {/* Datos del Médico */}
      <div className="tarjeta">
        <p style={{ margin: 0, fontWeight: 600 }}>{medico.nombre}</p>
        <p className="muted" style={{ margin: '4px 0 0' }}>{medico.email}</p>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          Matrícula {medico.matricula.tipo} {medico.matricula.numero} ({medico.matricula.jurisdiccion})
        </p>
      </div>

      {/* Honorarios y Liquidación */}
      <div className="tarjeta">
        <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Honorarios del Período</h3>
        
        {/* Selector de Fechas */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <div style={{ flex: 1 }}>
            <label className="mini muted" style={{ display: 'block', marginBottom: '3px' }}>Desde</label>
            <input 
              type="date" 
              value={desde} 
              onChange={e => setDesde(e.target.value)} 
              style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="mini muted" style={{ display: 'block', marginBottom: '3px' }}>Hasta</label>
            <input 
              type="date" 
              value={hasta} 
              onChange={e => setHasta(e.target.value)} 
              style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {cargando ? (
          <p className="mini muted" style={{ textAlign: 'center', padding: '10px 0' }}>Cargando honorarios...</p>
        ) : error ? (
          <p className="mini roja" style={{ textAlign: 'center', padding: '10px 0' }}>{error}</p>
        ) : (
          <>
            {/* Total Ganado */}
            <div className="fila" style={{ backgroundColor: '#f1f8e9', padding: '10px', borderRadius: '6px', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, color: '#33691e', fontSize: '13px' }}>Honorarios acumulados:</span>
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#2e7d32' }}>
                {formatearMonedaArs(datosHonorarios?.totalGanadoCentavos ?? 0)}
              </span>
            </div>
            
            {/* Estadísticas de Visita */}
            <div className="fila mini" style={{ marginBottom: '4px' }}>
              <span className="muted">Total visitas realizadas:</span>
              <span style={{ fontWeight: 600 }}>{datosHonorarios?.estadisticas.totalVisitas}</span>
            </div>
            {datosHonorarios?.estadisticas.visitasSinHonorario !== undefined && datosHonorarios.estadisticas.visitasSinHonorario > 0 && (
              <div className="fila mini" style={{ marginBottom: '10px' }}>
                <span className="roja">Visitas sin honorario configurado:</span>
                <span className="roja" style={{ fontWeight: 600 }}>{datosHonorarios.estadisticas.visitasSinHonorario}</span>
              </div>
            )}

            {/* Listado de Controles */}
            <h4 style={{ margin: '14px 0 6px 0', fontSize: '12px', fontWeight: 600 }}>Detalle de visitas completadas</h4>
            {datosHonorarios?.controles.length === 0 ? (
              <p className="mini muted" style={{ textAlign: 'center', padding: '10px 0', border: '1px dashed #eee', borderRadius: '4px' }}>
                No se registraron visitas en este período.
              </p>
            ) : (
              <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                {datosHonorarios?.controles.map((c) => (
                  <div key={c.controlId} style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: '11px' }}>
                    <div className="fila">
                      <span style={{ fontWeight: 500 }}>{c.trabajadorNombre}</span>
                      <span style={{ fontWeight: 600, color: c.montoCentavos !== null ? '#2e7d32' : '#c62828' }}>
                        {c.montoCentavos !== null ? formatearMonedaArs(c.montoCentavos) : 'Sin tarifa'}
                      </span>
                    </div>
                    <div className="fila muted" style={{ marginTop: '2px', fontSize: '10px' }}>
                      <span>{c.empresaNombre} ({c.tipoControlNombre})</span>
                      <span>{new Date(c.fechaControl).toLocaleDateString('es-AR')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Emisión de la factura al cliente (ADR-0010) */}
        <div style={{ borderTop: '1px solid #eee', marginTop: 14, paddingTop: 12 }}>
          {facturaError && <p className="mini roja" style={{ margin: '0 0 8px' }}>{facturaError}</p>}
          <button
            className="primario"
            style={{ width: '100%' }}
            onClick={() => void emitirFactura()}
            disabled={emitiendo || yaFacturado || !datosHonorarios || datosHonorarios.totalGanadoCentavos === 0}
          >
            {emitiendo ? 'Emitiendo…' : yaFacturado ? 'Período ya facturado' : 'Emitir factura al cliente'}
          </button>
          {facturas.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: 600 }}>Mis facturas</h4>
              {facturas.map((f) => (
                <div key={f.id} className="fila" style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: '11px' }}>{f.numero}</div>
                    <div className="mini muted">{f.periodo.desde} → {f.periodo.hasta} · {f.visitas} visita(s)</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`insignia ${COLOR_ESTADO_FACTURA[f.estado]}`}>{ETIQUETA_ESTADO_FACTURA[f.estado]}</span>
                    <button className="fantasma" style={{ width: 'auto', fontSize: '11px' }} onClick={() => void verFactura(f.id)}>PDF</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Otras Tarjetas Existentes */}
      <div className="tarjeta">
        <div className="fila">
          <span>Validación REFEPS</span>
          {medico.refeps.validada ? <span className="insignia verde">Validada</span> : <span className="insignia roja">Pendiente</span>}
        </div>
        <p className="mini" style={{ margin: '6px 0 0' }}>
          {medico.refeps.consultadaEn ? `Última consulta: ${new Date(medico.refeps.consultadaEn).toLocaleString('es-AR')}` : 'Sin consultar'}
        </p>
      </div>

      <div className="tarjeta">
        <div className="fila">
          <span>Firma digital remota</span>
          {medico.firmaEnrolado ? <span className="insignia verde">Enrolado</span> : <span className="insignia ambar">Trámite pendiente</span>}
        </div>
        <p className="mini" style={{ margin: '6px 0 0' }}>Proveedor: {medico.firmaProveedor}</p>
      </div>

      <div className="tarjeta">
        <div className="fila">
          <span>Clave de firma</span>
          {medico.claveFirmaDefinida
            ? <span className="insignia verde">Definida</span>
            : <span className="insignia ambar">No definida</span>}
        </div>
        <p className="mini" style={{ margin: '6px 0 0' }}>
          Definí una clave numérica personal de 6 dígitos para firmar tus constancias al instante, sin esperar el
          código por email. Solo vos la conocés.
        </p>
        {claveOk && <p className="mini" style={{ color: 'var(--verde)', marginTop: 6 }}>{claveOk}</p>}
        {formClave === 'oculto' ? (
          <div className="row-acciones" style={{ marginTop: 10 }}>
            <button className="secundario" onClick={() => abrirFormClave('definir')}>
              {medico.claveFirmaDefinida ? 'Cambiar clave' : 'Definir clave de firma'}
            </button>
            {medico.claveFirmaDefinida && (
              <button className="fantasma" onClick={() => abrirFormClave('quitar')}>Quitar clave</button>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            <div className="campo">
              <label>Tu contraseña de Ausentia</label>
              <input
                type="password"
                value={clavePassword}
                onChange={(e) => setClavePassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            {formClave === 'definir' && (
              <>
                <div className="campo">
                  <label>Nueva clave (6 dígitos)</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={clave1}
                    onChange={(e) => setClave1(e.target.value.replace(/\D/g, ''))}
                    placeholder="······"
                  />
                </div>
                <div className="campo">
                  <label>Repetí la clave</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={clave2}
                    onChange={(e) => setClave2(e.target.value.replace(/\D/g, ''))}
                    placeholder="······"
                  />
                </div>
              </>
            )}
            {formClave === 'quitar' && (
              <p className="mini" style={{ color: 'var(--rojo)' }}>
                Vas a quitar tu clave de firma: tus próximas firmas volverán al código por email.
              </p>
            )}
            {claveError && <div className="error-box">{claveError}</div>}
            <div className="row-acciones" style={{ marginTop: 8 }}>
              <button
                className="primario"
                onClick={guardarClave}
                disabled={
                  claveGuardando ||
                  !clavePassword ||
                  (formClave === 'definir' && (clave1.length !== 6 || clave2.length !== 6))
                }
              >
                {claveGuardando ? 'Guardando…' : formClave === 'definir' ? 'Guardar clave' : 'Quitar clave'}
              </button>
              <button className="fantasma" onClick={() => setFormClave('oculto')} disabled={claveGuardando}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="aviso-info">
        <span>🔐</span>
        <span>Sin matrícula validada ante REFEPS la firma queda bloqueada, pero la carga de datos nunca se interrumpe (HU-01).</span>
      </div>

      <button className="peligro" style={{ width: '100%', marginTop: 8 }} onClick={() => { salir(); navegar('/'); }}>
        Cerrar sesión
      </button>
    </div>
  );
}
